import { Spacer, VDBText } from "@components/atoms";
import React, { useCallback, useEffect, useMemo } from "react";
import styled from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTheme } from "styled-components/native";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { PriceModes } from "../PriceModeCard/types";
import { useUserCurrency } from "../../../hooks/getCurrency";
import { useTranslation } from "react-i18next";
import { formatItemValue, PriceContainerTypes } from "./types";
import { useSelector } from "react-redux";
import { DefaultThemeValueType } from "../../../../styled";
import currency from "currency.js";
import { getRequestType, RequestTypes } from "@scenes/requests/utils/utils";
import {
  addPercentage,
  formatNumberWithPrecision,
} from "@utils/product/productFormatters";
import StrikePrice from "../StrikePrice";
import { ProductCategory, ViewType } from "@utils/constants";
import QuantitySelector from "../QuantitySelector";
import { useAllowQuantityOnBulk } from "@redux/slices/globalSlice";
import { useUpdateBulkCartQuantity } from "@apis/cartRequest";
import { CartTypeEnum } from "@utils/product/searchResultConstants";
import { getStockItemType } from "@scenes/searchResults/Utils";
import { isMeleeItem } from "@utils/product/productValidators";
import { debounce } from "lodash";
import { hasUpdatedPrice } from "./utils";

const PriceContainer = ({
  mItem,
  priceDetailsDataOptions,
  fromBulkList = false,
  isTotalOfferDiscountApplied,
  viewType,
  requestType = "",
  hasAnyOfferDetails = false,
  fromRequestDetail,
  childValues,
  stockType,
  fromRequestModify = false,
  saveBulkItemDetails,
}: PriceContainerTypes) => {
  const theme = useTheme();
  const userInfo = useUserInfo();
  const priceMode = userInfo?.user?.price_mode;
  const { t } = useTranslation();
  const userCurrency = useUserCurrency();
  const typography = useTypographyStore();
  const isCustomApp = useSelector((state) => state?.appState?.isCustomApp);
  const allowQuantityOnBulk = useAllowQuantityOnBulk();
  const updateQuantityMutation = useUpdateBulkCartQuantity();
  const cartItemId = String((mItem as any)?.cart_item_id);
  const stockItemType = getStockItemType(mItem?.type as ProductCategory);

  const debouncedUpdateQuantity = useMemo(
    () =>
      debounce((newQuantity: number) => {
        updateQuantityMutation.mutate({
          itemId: cartItemId,
          cart_type: CartTypeEnum.BULK_REQUEST_TYPE,
          stock_type: stockItemType,
          price_mode: priceMode,
          quantity: newQuantity,
        });
      }, 300),
    [cartItemId, stockItemType, priceMode, stockType, updateQuantityMutation]
  );

  useEffect(() => {
    return () => {
      debouncedUpdateQuantity.cancel();
    };
  }, [debouncedUpdateQuantity]);

  const handleQuantityChange = useCallback(
    (newQuantity: number) => {
      if (!fromRequestModify) {
        debouncedUpdateQuantity(newQuantity);
      } else {
        // Pass `true` as the 3rd arg (fromParcel-style) so the wrapped
        // _saveBulkItemDetails in BulkInitSummaryList does not close the
        // EditBulkItem modal mid-edit when only quantity changes.
        saveBulkItemDetails?.(
          mItem?.id,
          { quantity: String(newQuantity) },
          true
        );
      }
    },
    [
      mItem,
      stockType,
      debouncedUpdateQuantity,
      saveBulkItemDetails,
      fromRequestModify,
    ]
  );
  const hasSpecialPrice =
    isCustomApp && mItem?.special_price !== null && !fromBulkList;
  const removeOfferValue =
    viewType !== ViewType.BUYER &&
    getRequestType(requestType) === RequestTypes.Quote &&
    isTotalOfferDiscountApplied;

  const showSellerDiscountView =
    viewType !== ViewType.BUYER &&
    getRequestType(requestType) === RequestTypes.Quote &&
    hasAnyOfferDetails;

  const priceUpdated = hasUpdatedPrice(mItem);
  const hideListPrice = priceUpdated
    ? viewType === ViewType.SELLER &&
      (getRequestType(requestType) === RequestTypes.Quote ||
        requestType === RequestTypes.Quote)
    : false;
  const showListPrice = (item) => {
    if (
      mItem?.IndividualItemEdit === false ||
      requestType === RequestTypes.Questions
    )
      return false;

    const key = item?.response_key;
    const updatedPrice = mItem?.[`updated_${key}`];

    const pricePerCarat = mItem?.updated_price_per_carat;
    const totalPrice = mItem?.updated_total_sales_price;
    const discountPercent = mItem?.updated_discount_percent;

    const isDiscount = key === "discount_percent";

    const isEmpty = (val) => val == null || val === "" || val === "-";

    const isEmptyUpdatedPrice = isEmpty(updatedPrice);
    const isEmptyPricePerCarat = isEmpty(pricePerCarat);
    const isEmptyTotalPrice = isEmpty(totalPrice);
    const isEmptyDiscountPercent = isEmpty(discountPercent);

    const anyPriceUpdated =
      !isEmptyPricePerCarat || !isEmptyTotalPrice || !isEmptyDiscountPercent;

    if (isDiscount && (updatedPrice === "" || showListDetail())) {
      return updatedPrice === "" || showListDetail();
    }

    if (isDiscount && isEmptyUpdatedPrice) {
      return false;
    }

    if (!updatedPrice) return anyPriceUpdated;

    if (
      isDiscount &&
      isEmptyUpdatedPrice &&
      isEmptyPricePerCarat &&
      isEmptyTotalPrice
    ) {
      return false;
    }

    const precision = userInfo?.roundUpPrices() && !isMeleeItem(mItem) ? 0 : 2;

    const {
      total_sales_price: originalTotalPrice,
      price_per_carat: originalPricePerCarat,
      discount_percent: originalDiscount,
      currency_symbol,
    } = mItem || {};

    if (originalTotalPrice === totalPrice && key === "price_per_carat") {
      return (
        formatNumberWithPrecision(originalPricePerCarat, { precision: 0 }) !==
        formatNumberWithPrecision(pricePerCarat, { precision: 0 })
      );
    }

    if (isDiscount && originalTotalPrice !== totalPrice) {
      return originalDiscount == null
        ? true
        : originalDiscount !== discountPercent;
    }

    return (
      currency(updatedPrice?.toString(), {
        symbol: currency_symbol,
        precision,
      }).format() !==
      currency(mItem?.[key]?.toString(), {
        symbol: currency_symbol,
        precision,
      }).format()
    );
  };

  const showListDetail = () => {
    const match = childValues?.find((item) => item?.parent_id === mItem?.id);
    const individualItemEditValue = match ? match?.IndividualItemEdit : null;
    return individualItemEditValue;
  };

  return (
    <ThirdContainer
      theme={theme}
      borderRadius={mItem?.comments ? 0 : SpaceType.Basic}
      allowQuantityOnBulk={allowQuantityOnBulk}
    >
      {allowQuantityOnBulk &&
      (mItem?.type as ProductCategory) === ProductCategory.JEWELRY ? (
        fromRequestDetail ? (
          <>
            <QuantityContainer>
              <VDBText color={theme["core-06"]} type={typography["Body-R"]}>
                {t("orderCart.itemCard.quantityLabel.text")}
              </VDBText>
              <VDBText color={theme["core-05"]} type={typography["Body-M"]}>
                {mItem?.quantity || 1}
              </VDBText>
            </QuantityContainer>
          </>
        ) : (
          <QuantitySelector
            min={1}
            max={99}
            label=""
            onChange={handleQuantityChange}
            defaultValue={Number(mItem?.quantity) || 1}
          />
        )
      ) : null}
      <PriceMainContainer>
        {priceMode !== PriceModes.GREEN &&
          priceDetailsDataOptions?.map((item: any, index: number) => {
            return (
              <React.Fragment key={index}>
                {(hasSpecialPrice ||
                  (showListPrice(item) &&
                    !removeOfferValue &&
                    !showSellerDiscountView &&
                    !hideListPrice &&
                    fromBulkList)) &&
                  index === 0 && (
                    <TextContainer>
                      <VDBText
                        color={theme["core-06"]}
                        type={typography["Minor-R"]}
                      >
                        {t(
                          hasSpecialPrice
                            ? `searchResult.grid.retailPrice.text`
                            : `bulkRequest.requestItem.listed.text`
                        )}
                      </VDBText>
                      <Spacer size={SpaceType.ExtraSmall} />
                      <VDBText
                        color={theme["core-06"]}
                        type={typography["Minor-R"]}
                      >
                        {t(
                          hasSpecialPrice
                            ? `searchResult.grid.specialPrice.text`
                            : `bulkRequest.requestItem.offer.text`
                        )}
                      </VDBText>
                    </TextContainer>
                  )}
                <ThirdContainerCell>
                  <ThirdContainerLabel>
                    <VDBText
                      color={theme["core-06"]}
                      type={typography["Minor-R"]}
                    >
                      {t(`${item.label}`)}
                    </VDBText>
                  </ThirdContainerLabel>
                  <ThirdContainerValue
                    theme={theme}
                    fromBulkList={fromBulkList}
                    hasSpecialPrice={hasSpecialPrice}
                  >
                    {hasSpecialPrice &&
                    item.response_key === "total_sales_price" ? (
                      <StrikePrice
                        value={formatItemValue(
                          isCustomApp,
                          item.response_key,
                          mItem,
                          userCurrency,
                          userInfo,
                          t
                        )}
                        textColor={theme["error-01"]}
                        lineColor={theme["error-01"]}
                        typography={typography["Minor-M"]}
                        align={"right"}
                      />
                    ) : !hideListPrice &&
                      (!showSellerDiscountView ||
                        (fromRequestDetail && showListDetail() === false)) ? (
                      <VDBText
                        color={theme["core-05"]}
                        type={typography["Minor-M"]}
                        textAlign="right"
                      >
                        {allowQuantityOnBulk &&
                        (mItem?.type as ProductCategory) ===
                          ProductCategory.JEWELRY
                          ? formatItemValue(
                              isCustomApp,
                              item.response_key,
                              mItem,
                              userCurrency,
                              userInfo,
                              t,
                              undefined,
                              true
                            )
                          : formatItemValue(
                              isCustomApp,
                              item.response_key,
                              mItem,
                              userCurrency,
                              userInfo,
                              t
                            )}
                      </VDBText>
                    ) : (
                      <></>
                    )}
                    {hasSpecialPrice && (
                      <>
                        <Spacer size={SpaceType.ExtraSmall} />
                        <VDBText
                          color={theme["core-05"]}
                          type={typography["Minor-M"]}
                          textAlign="right"
                        >
                          {item.response_key === "discount_percent" ||
                          item.response_key === "price_per_carat"
                            ? "-"
                            : currency(
                                item.response_key === "total_sales_price"
                                  ? mItem?.total_sales_price
                                  : mItem?.[`${item.response_key}`],
                                {
                                  symbol:
                                    item.response_key === "price_per_carat" ||
                                    item.response_key === "total_sales_price"
                                      ? userCurrency
                                      : "",
                                  precision:
                                    item.response_key === "total_sales_price" &&
                                    isCustomApp
                                      ? userInfo?.roundUpPrices() &&
                                        !isMeleeItem(mItem)
                                        ? 0
                                        : 2
                                      : 0,
                                }
                              ).format()}
                        </VDBText>
                      </>
                    )}
                    {!hasSpecialPrice &&
                      showListPrice(item) &&
                      (!removeOfferValue || showSellerDiscountView) &&
                      fromBulkList && (
                        <>
                          <Spacer size={SpaceType.ExtraSmall} />
                          <VDBText
                            color={theme["core-05"]}
                            type={typography["Minor-M"]}
                            textAlign="right"
                          >
                            {item.response_key === "discount_percent"
                              ? mItem?.IndividualItemEdit &&
                                (mItem?.discount_percent == null ||
                                  mItem?.discount_percent === "0.0")
                                ? "-"
                                : addPercentage(
                                    mItem?.[`updated_${item.response_key}`]
                                  ) || "-"
                              : currency(
                                  mItem?.[`updated_${item.response_key}`],
                                  {
                                    symbol:
                                      item.response_key === "price_per_carat" ||
                                      item.response_key === "total_sales_price"
                                        ? userCurrency
                                        : "",
                                    precision:
                                      item.response_key ===
                                        "total_sales_price" && isCustomApp
                                        ? userInfo?.roundUpPrices() &&
                                          !isMeleeItem(mItem)
                                          ? 0
                                          : 2
                                        : 0,
                                  }
                                ).format() || "-"}
                          </VDBText>
                        </>
                      )}
                  </ThirdContainerValue>
                </ThirdContainerCell>
                {index < priceDetailsDataOptions.length - 1 && (
                  <Spacer size={SpaceType.Double} />
                )}
              </React.Fragment>
            );
          })}
      </PriceMainContainer>
    </ThirdContainer>
  );
};

const ThirdContainer = styled.View<{
  borderRadius: number;
  allowQuantityOnBulk: boolean;
}>`
  width: auto;
  background-color: ${({ theme }) => theme["core-01"]};
  flex-direction: row;
  align-items: center;
  justify-content: ${({ allowQuantityOnBulk }) =>
    allowQuantityOnBulk ? "space-between" : "flex-end"};
  padding: ${SpaceType.Basic}px;
  border-bottom-left-radius: ${({ borderRadius }) => borderRadius}px;
  border-bottom-right-radius: ${({ borderRadius }) => borderRadius}px;
`;
const TextContainer = styled.View`
  flex-direction: column;
  padding-top: ${SpaceType.Double}px;
  padding-right: ${SpaceType.ExtraLarge}px;
`;
const PriceMainContainer = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
`;

const ThirdContainerCell = styled.View<any>`
  flex-direction: column;
  width: ${({ width }) => width}px;
`;

const ThirdContainerLabel = styled.View<any>`
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  width: ${({ width }) => width}px;
  margin-bottom: 4px;
`;

const ThirdContainerValue = styled.View<{
  fromBulkList?: boolean;
  width?: any;
  theme?: DefaultThemeValueType;
  hasSpecialPrice?: boolean;
}>`
  flex-direction: ${({ fromBulkList, hasSpecialPrice }) =>
    fromBulkList || hasSpecialPrice ? "column" : "row"};
  justify-content: flex-end;
  align-items: ${({ fromBulkList, hasSpecialPrice }) =>
    fromBulkList || hasSpecialPrice ? "" : "center"};
  width: ${({ width }) => width}px;
`;

const QuantityContainer = styled.View`
  flex-direction: row;
  align-items: center;
  gap: ${SpaceType.ExtraSmall}px;
`;

export default React.memo(PriceContainer);
